import { Platform } from "@src/mobile/utils/platform";
import { useEffect } from "react";

import { getExistingSessionEmail } from "../../cognito/cognitoAuth";
import { clearMagicLinkEmail, getMagicLinkEmail } from "../../cognito/magicLinkEmailStore";
import {
  clearRedirectInProgress,
  hasCognitoTokens,
  REDIRECT_IN_PROGRESS_KEY,
} from "../../cognito/redirectInProgress";
import { type AuthState } from "../types";

const SESSION_CHECK_TIMEOUT_MS = 20_000;

const MOBILE_MISSING_EMAIL_MESSAGE =
  "This sign-in link is missing required information. Please request a fresh login link from the app.";

interface UseResolveInitialAuthStateArguments {
  payload: string | null;
  handleContinue: (emailToUse: string, source: "cache" | "prompt") => Promise<void>;
  handleGoToDashboard: () => void;
  setAuthState: (state: AuthState) => void;
  setError: (error: string | undefined) => void;
  setExistingEmail: (email: string | undefined) => void;
}

/**
 * Runs the initial session-check + email-resolution effect on mount.
 * Decides which AuthState we should land in based on:
 *
 *  1. A post-redemption remount shortcut (sessionStorage flag).
 *  2. Whether the user already has an active Cognito session.
 *  3. Whether we have a cached magic-link email (from request time).
 *  4. Whether we're inside the native Capacitor app (no prompt UI).
 *
 * Extracted from AuthRedirect.tsx to keep that file under the max-lines limit.
 */
export function useResolveInitialAuthState({
  payload,
  handleContinue,
  handleGoToDashboard,
  setAuthState,
  setError,
  setExistingEmail,
}: UseResolveInitialAuthStateArguments): void {
  useEffect(() => {
    if (!payload) {
      setAuthState("invalid");
      return;
    }

    // Post-redemption remount shortcut. When the original AuthRedirect
    // instance is mid-flight (between `dispatch(initCognitoSession())` and
    // `history.replace(target)`), the parent route tree's `isAuthorized`
    // flip causes us to remount in the authed branch. Stay in the loading
    // state and navigate directly — the original instance is racing us to
    // the same target. Without this we'd render the "already authenticated"
    // consent screen for a few hundred ms before being unmounted.
    if (sessionStorage.getItem(REDIRECT_IN_PROGRESS_KEY) && hasCognitoTokens()) {
      clearRedirectInProgress();
      handleGoToDashboard();
      return;
    }

    let cancelled = false;

    void (async () => {
      const sessionEmail = await Promise.race<string | undefined>([
        getExistingSessionEmail(),
        new Promise<undefined>((resolve) =>
          setTimeout(() => resolve(undefined), SESSION_CHECK_TIMEOUT_MS)
        ),
      ]);

      if (cancelled) {
        return;
      }

      // Email resolution mirrors cbh-mobile-app: TTL'd dedicated cache from
      // request time, prompt fallback when the cache misses. URL `?email=`
      // is intentionally not honored — Cognito's Lambda emits payload-only
      // links and accepting an email from the URL would defeat the
      // "shared link is useless without knowing the recipient's email"
      // property the prompt is meant to enforce.
      const cachedEmail = getMagicLinkEmail();

      if (sessionEmail) {
        setExistingEmail(sessionEmail);
        // Same-user case: the user clicked their own magic link while
        // already signed in. There's no decision to make — just navigate.
        // (The post-redemption remount flash is handled separately by the
        // REDIRECT_IN_PROGRESS_KEY shortcut above.)
        if (cachedEmail && sessionEmail.toLowerCase() === cachedEmail.toLowerCase()) {
          clearMagicLinkEmail();
          handleGoToDashboard();
          return;
        }
        setAuthState("already-authenticated");
        return;
      }

      if (Platform.isMobile) {
        // Native app: no UI for prompting. Auto-continue using the cached
        // email from request time. If we don't have one (cross-device tap,
        // expired cache, etc.) direct them back to login to request a fresh
        // link.
        if (cachedEmail) {
          void handleContinue(cachedEmail, "cache");
        } else {
          setError(MOBILE_MISSING_EMAIL_MESSAGE);
          setAuthState("error");
        }
        return;
      }

      // Web, no session: cache hit → silent confirm; cache miss → prompt.
      if (cachedEmail) {
        setAuthState("confirm");
      } else {
        setAuthState("email-input");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [payload, handleContinue, handleGoToDashboard, setAuthState, setError, setExistingEmail]);
}
